import React, { useEffect, useState } from 'react';
import { InputContainer, Label, InputWrapper, AdornmentWrapper, StyledInput, ErrorMessage, HelperText } from './styles';
import { InputProps } from './types';

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({
    variant = 'default',
    size = 'default',
    label,
    labelStyle = 'default',
    error,
    helperText,
    id,
    className,
    startAdornment,
    endAdornment,
    style,
    validateEmail = false,
    emailErrorMessage,
    ...rest
  }, ref) => {
    const {
      type,
      inputMode: providedInputMode,
      onChange,
      onBlur,
      value,
      ...inputProps
    } = rest;

    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    const [touched, setTouched] = useState(false);
    const [emailError, setEmailError] = useState('');

    const shouldValidateEmail = validateEmail || type === 'email';
    const validationMessage = emailErrorMessage || 'Enter a valid email address';

    const runEmailValidation = (val: string) => {
      if (!shouldValidateEmail) return '';
      if (!val) return '';
      return EMAIL_REGEX.test(val) ? '' : validationMessage;
    };

    useEffect(() => {
      if (shouldValidateEmail && touched) {
        const nextValue = typeof value === 'string' ? value : '';
        setEmailError(runEmailValidation(nextValue));
      }
    }, [value, shouldValidateEmail, touched, validationMessage]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (shouldValidateEmail && touched) {
        setEmailError(runEmailValidation(e.target.value));
      }
      onChange?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (shouldValidateEmail) {
        setTouched(true);
        setEmailError(runEmailValidation(e.target.value));
      }
      onBlur?.(e);
    };

    const effectiveError = error || emailError || undefined;
    const effectiveVariant = effectiveError ? 'error' : variant;
    const effectiveInputMode = providedInputMode || (shouldValidateEmail ? 'email' : undefined);
    const effectiveType = type || (shouldValidateEmail ? 'email' : undefined);

    return (
      <InputContainer className={className} style={style}>
        {label && <Label htmlFor={inputId} $labelStyle={labelStyle}>{label}</Label>}
        <InputWrapper $variant={effectiveVariant} $size={size} $disabled={inputProps.disabled}>
          {startAdornment && <AdornmentWrapper>{startAdornment}</AdornmentWrapper>}
          <StyledInput
            ref={ref}
            id={inputId}
            type={effectiveType}
            inputMode={effectiveInputMode}
            $size={size}
            $hasStartAdornment={!!startAdornment}
            $hasEndAdornment={!!endAdornment}
            onChange={handleChange}
            onBlur={handleBlur}
            value={value}
            {...inputProps}
          />
          {endAdornment && <AdornmentWrapper>{endAdornment}</AdornmentWrapper>}
        </InputWrapper>
        {effectiveError && <ErrorMessage>{effectiveError}</ErrorMessage>}
        {!effectiveError && helperText && <HelperText>{helperText}</HelperText>}
      </InputContainer>
    );
  }
);

Input.displayName = 'Input';

export type { InputProps, InputVariant, InputSize } from './types';
